(() => {
  const SELECTOR = '[data-game-day-vote-section]';

  if (window.PuraGameDayVoteCapture?.initialize) {
    window.PuraGameDayVoteCapture.initialize(document);
    return;
  }

  class GameDayVoteCapture {
    constructor(root) {
      if (root.__gameDayVoteCapture) return root.__gameDayVoteCapture;
      root.__gameDayVoteCapture = this;
      this.root = root;
      this.selectedVote = '';
      this.selectedVoteKey = '';
      this.isSubmitting = false;
      this.lastCommit = null;
      this.captureScrollTimer = null;
      this.onClick = this.handleClick.bind(this);
      this.onSubmit = this.handleSubmit.bind(this);
      root.addEventListener('click', this.onClick);
      this.form?.addEventListener('submit', this.onSubmit);
    }

    get buttons() { return Array.from(this.root.querySelectorAll('[data-game-day-vote-button]')); }
    get form() { return this.root.querySelector('[data-game-day-vote-form]'); }
    get emailInput() { return this.form?.querySelector('[data-game-day-vote-email]'); }
    get consentInput() { return this.form?.querySelector('[data-game-day-newsletter-consent]'); }
    get submitButton() { return this.form?.querySelector('[data-game-day-vote-submit]'); }
    get formError() { return this.root.querySelector('[data-game-day-vote-form-error]'); }
    get propertyName() { return this.root.dataset.klaviyoProfileProperty || 'Game Day Pairing Vote'; }
    get voteKeyPropertyName() { return this.root.dataset.klaviyoVoteKeyProperty || 'Game Day Pairing Vote Key'; }
    get campaignPropertyName() { return this.root.dataset.klaviyoCampaignProperty || 'Game Day Vote Campaign'; }
    get campaignSlug() { return this.root.dataset.voteCampaignSlug || ''; }
    get eventName() { return this.root.dataset.klaviyoVoteEvent || 'Game Day Vote Submitted'; }
    get klaviyoTimeoutMs() {
      const value = Number(this.root.dataset.klaviyoTimeoutMs);
      return Number.isFinite(value) && value > 0 ? value : 12000;
    }

    handleClick(event) {
      const button = event.target.closest('[data-game-day-vote-button]');
      if (!button || !this.root.contains(button) || this.isSubmitting) return;
      const value = button.dataset.value || button.textContent.trim();
      const voteKey = button.dataset.voteKey || '';
      const previous = this.selectedVote;
      const next = previous === value ? '' : value;
      const operation = !previous && next ? 'add' : previous && !next ? 'remove' : 'switch';
      this.setVote(next, next ? voteKey : '', previous, operation);
    }

    setVote(value, voteKey, previous, operation) {
      if (this.captureScrollTimer) window.clearTimeout(this.captureScrollTimer);
      this.selectedVote = value;
      this.selectedVoteKey = voteKey;
      this.root.dataset.selectedVote = value;
      this.root.dataset.selectedVoteKey = voteKey;
      this.root.classList.toggle('is-voted', Boolean(value));
      this.buttons.forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.value === value)));
      this.clearSelectionError();
      this.clearFormError();

      const selection = this.root.querySelector('[data-game-day-vote-selection]');
      if (selection) selection.textContent = value
        ? `${this.root.dataset.selectionPrefix || 'Selected: '}${value}. This is not a vote yet. Enter your email and press Submit to vote.`
        : '';

      const heading = this.root.querySelector('[data-game-day-capture-heading]');
      const text = this.root.querySelector('[data-game-day-capture-text]');
      if (heading) heading.textContent = value
        ? (this.root.dataset.selectedHeadingTemplate || 'Selected: __VOTE__.').replace('__VOTE__', value)
        : (this.root.dataset.defaultHeading || '');
      if (text) text.textContent = value ? (this.root.dataset.selectedText || '') : (this.root.dataset.defaultText || '');

      this.root.dispatchEvent(new CustomEvent('game-day:vote-change', {
        bubbles: true,
        detail: { sectionId: this.root.dataset.sectionId, vote: value, voteKey, previousVote: previous, operation },
      }));

      if (value && window.matchMedia('(max-width: 700px)').matches) {
        this.captureScrollTimer = window.setTimeout(() => {
          this.captureScrollTimer = null;
          if (this.selectedVote === value) this.root.querySelector('[data-game-day-capture]')?.scrollIntoView({
            behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
            block: 'start',
          });
        }, 350);
      }
    }

    setSelectionError() {
      const error = this.root.querySelector('[data-game-day-vote-selection-error]');
      if (!error) return;
      error.textContent = this.root.dataset.selectionRequiredMessage || 'Select a pairing before submitting your vote.';
      error.hidden = false;
      this.emailInput?.setAttribute('aria-describedby', `${error.id} ${this.formError?.id || ''}`.trim());
    }

    clearSelectionError() {
      const error = this.root.querySelector('[data-game-day-vote-selection-error]');
      if (error) { error.textContent = ''; error.hidden = true; }
    }

    setFormError(message) {
      if (!this.formError) return;
      this.formError.textContent = message;
      this.formError.hidden = false;
      this.emailInput?.setAttribute('aria-describedby', `${this.root.querySelector('[data-game-day-vote-selection-error]')?.id || ''} ${this.formError.id}`.trim());
    }

    clearFormError() {
      if (this.formError) { this.formError.textContent = ''; this.formError.hidden = true; }
      this.emailInput?.removeAttribute('aria-invalid');
    }

    setLoading(loading) {
      this.isSubmitting = loading;
      this.form?.setAttribute('aria-busy', String(loading));
      if (this.submitButton) {
        this.submitButton.disabled = loading;
        this.submitButton.setAttribute('aria-disabled', String(loading));
      }
    }

    commitSignature(email) { return `${email.trim().toLowerCase()}|${this.campaignSlug}|${this.selectedVoteKey}`; }

    async handleSubmit(event) {
      event.preventDefault();
      if (this.isSubmitting) return;
      this.clearFormError();
      if (!this.selectedVote || !this.selectedVoteKey) {
        this.setSelectionError();
        this.emailInput?.focus();
        return;
      }

      const email = this.emailInput?.value.trim() || '';
      if (!this.emailInput?.validity.valid || !email) {
        this.emailInput?.setAttribute('aria-invalid', 'true');
        this.setFormError('Enter a valid email address.');
        this.emailInput?.focus();
        return;
      }

      const wantsNewsletter = Boolean(this.consentInput?.checked);
      const signature = this.commitSignature(email);
      this.setLoading(true);
      try {
        if (this.lastCommit?.signature !== signature) {
          await this.submitVote(email);
          this.lastCommit = { signature, newsletterSubscribed: false };
        }
        if (wantsNewsletter && !this.lastCommit?.newsletterSubscribed) {
          await this.subscribeToNewsletter(email);
          this.lastCommit.newsletterSubscribed = true;
        }
        this.markSubmitted(wantsNewsletter ? 'Vote submitted and newsletter subscription confirmed.' : 'Vote submitted.');
      } catch (error) {
        this.setFormError(error instanceof Error ? error.message : 'We could not submit your vote. Please try again.');
      } finally {
        this.setLoading(false);
      }
    }

    async submitVote(email) {
      const companyId = this.root.dataset.klaviyoCompanyId;
      if (!companyId) throw new Error('Voting is temporarily unavailable. Please try again shortly.');

      const response = await this.awaitKlaviyo('save your vote', fetch(
        `https://a.klaviyo.com/client/events?company_id=${encodeURIComponent(companyId)}`,
        {
          method: 'POST',
          headers: {
            Accept: 'application/vnd.api+json',
            'Content-Type': 'application/vnd.api+json',
            revision: '2026-07-15',
          },
          body: JSON.stringify({
            data: {
              type: 'event',
              attributes: {
                properties: {
                  campaign: this.campaignSlug,
                  vote_key: this.selectedVoteKey,
                  vote: this.selectedVote,
                },
                metric: {
                  data: { type: 'metric', attributes: { name: this.eventName } },
                },
                profile: {
                  data: {
                    type: 'profile',
                    attributes: {
                      email,
                      properties: {
                        [this.propertyName]: this.selectedVote,
                        [this.voteKeyPropertyName]: this.selectedVoteKey,
                        [this.campaignPropertyName]: this.campaignSlug,
                      },
                    },
                  },
                },
              },
            },
          }),
        },
      ));
      if (!response || response.status !== 202) {
        throw new Error('Voting is temporarily unavailable. Please try again shortly.');
      }
    }

    async awaitKlaviyo(action, operation) {
      let timeout;
      try {
        return await Promise.race([
          Promise.resolve(operation),
          new Promise((_, reject) => {
            timeout = window.setTimeout(() => reject(new Error(`We could not ${action} in time. Please try again.`)), this.klaviyoTimeoutMs);
          }),
        ]);
      } finally {
        if (timeout) window.clearTimeout(timeout);
      }
    }

    async subscribeToNewsletter(email) {
      const companyId = this.root.dataset.klaviyoCompanyId;
      const listId = this.root.dataset.klaviyoMarketingListId;
      if (!companyId || !listId) throw new Error('Your vote was recorded, but newsletter subscription is temporarily unavailable. Please retry.');
      const response = await fetch(`https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(companyId)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', revision: '2024-06-15' },
        body: JSON.stringify({
          data: {
            type: 'subscription',
            attributes: {
              custom_source: 'Game Day Vote - Optional Newsletter Consent',
              profile: { data: { type: 'profile', attributes: { email } } },
            },
            relationships: { list: { data: { type: 'list', id: listId } } },
          },
        }),
      });
      if (!response.ok) throw new Error('Your vote was recorded, but newsletter subscription could not be completed. Please retry.');
    }

    markSubmitted(message) {
      this.root.classList.add('is-submitted');
      const selection = this.root.querySelector('[data-game-day-vote-selection]');
      if (selection) selection.textContent = `Vote submitted for ${this.selectedVote}. Change your selection, then press Submit to vote to update it.`;
      const heading = this.root.querySelector('[data-game-day-capture-heading]');
      const text = this.root.querySelector('[data-game-day-capture-text]');
      if (heading) heading.textContent = (this.root.dataset.submittedHeadingTemplate || 'Vote submitted for __VOTE__.').replace('__VOTE__', this.selectedVote);
      if (text) text.textContent = this.root.dataset.submittedText || '';
      const status = this.root.querySelector('[data-game-day-vote-status]');
      if (status) status.textContent = message;
      this.clearSelectionError();
    }

    destroy() {
      if (this.captureScrollTimer) window.clearTimeout(this.captureScrollTimer);
      this.root.removeEventListener('click', this.onClick);
      this.form?.removeEventListener('submit', this.onSubmit);
      delete this.root.__gameDayVoteCapture;
    }
  }

  const initialize = (scope = document) => {
    if (scope.matches?.(SELECTOR)) new GameDayVoteCapture(scope);
    scope.querySelectorAll?.(SELECTOR).forEach((root) => new GameDayVoteCapture(root));
  };
  const unload = (scope) => {
    if (scope.matches?.(SELECTOR)) scope.__gameDayVoteCapture?.destroy();
    scope.querySelectorAll?.(SELECTOR).forEach((root) => root.__gameDayVoteCapture?.destroy());
  };

  window.PuraGameDayVoteCapture = { initialize };
  // Theme Editor can inject this asset after DOMContentLoaded. Initialize
  // immediately in that case; GameDayVoteCapture is idempotent per section.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize());
  } else {
    initialize(document);
  }
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => unload(event.target));
})();
